import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  Briefcase,
  Building,
  User,
  FileText,
  Clock,
  Calendar,
  CheckCircle2,
  AlertCircle,
  ShieldAlert,
  Edit,
  RefreshCw,
  Award,
  UserCheck,
  MapPin,
  Globe,
  FileCheck,
} from "lucide-react";
import {
  getEmployeeById,
  getEmploymentHistory,
  getEmploymentTypes,
  getWorkSchedules,
  getDepartments,
} from "../services/employmentService";
import EditEmployeeDrawer from "../component/EditEmployeeDrawer";
import ContractFormDrawer from "../../Contracts/component/ContractFormDrawer";
import { updateEmploymentContract, renewEmploymentContract } from "../../Contracts/services/employmentContractService";

export default function EmploymentProfilePage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [employee, setEmployee] = useState(location.state?.employee || null);
  const [history, setHistory] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [workSchedules, setWorkSchedules] = useState([]);
  const [loading, setLoading] = useState(!location.state?.employee);
  const [activeTab, setActiveTab] = useState("overview");

  // Drawer States
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isContractDrawerOpen, setIsContractDrawerOpen] = useState(false);
  const [isRenewing, setIsRenewing] = useState(false);

  useEffect(() => {
    const fetchAllDetails = async () => {
      if (!uuid) return;
      try {
        if (!employee) setLoading(true);

        const [empData, historyData, deptsData, typesData, schedulesData] = await Promise.all([
          getEmployeeById(uuid),
          getEmploymentHistory().catch(() => []),
          getDepartments().catch(() => []),
          getEmploymentTypes().catch(() => []),
          getWorkSchedules().catch(() => []),
        ]);

        if (empData) {
          setEmployee((prev) => ({ ...prev, ...empData }));
        }
        if (Array.isArray(historyData)) setHistory(historyData);
        if (Array.isArray(deptsData?.data?.data)) setDepartments(deptsData.data.data);
        if (Array.isArray(typesData?.data?.data)) setEmploymentTypes(typesData.data.data);
        if (Array.isArray(schedulesData?.data?.data)) setWorkSchedules(schedulesData.data.data);
      } catch (err) {
        console.warn("Employment profile fetch fallback:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllDetails();
  }, [uuid]);

  if (loading && !employee) {
    return (
      <div className="flex h-96 w-full items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-[#147db5]" />
          <p className="mt-3 text-xs font-semibold text-slate-500">Loading HR employment profile...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="p-8 text-center bg-slate-50 min-h-screen flex items-center justify-center">
        <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xs">
          <ShieldAlert className="mx-auto h-10 w-10 text-rose-500" />
          <h3 className="mt-3 text-base font-semibold text-slate-800">Employment Record Not Found</h3>
          <p className="mt-1 text-xs text-slate-500">The requested profile could not be loaded from the system.</p>
          <button
            type="button"
            onClick={() => navigate("/employment/employmentpage")}
            className="mt-5 rounded-lg bg-[#147db5] px-4 py-2 text-xs font-medium text-white hover:bg-[#116d9d]"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Helpers
  const getInitials = (name) => {
    if (!name) return "EMP";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const calculateTenure = (joiningDate) => {
    if (!joiningDate) return "—";
    const start = new Date(joiningDate);
    const now = new Date();
    if (isNaN(start.getTime())) return "—";
    let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    if (months < 0) return "Just Joined";
    const years = Math.floor(months / 12);
    const remMonths = months % 12;
    if (years === 0) return `${remMonths} Month${remMonths === 1 ? "" : "s"}`;
    return `${years} Yr${years === 1 ? "" : "s"} ${remMonths} Mo${remMonths === 1 ? "" : "s"}`;
  };

  return (
    <div className="min-h-full bg-slate-50/70 p-6 md:p-8 transition-all duration-300 ease-out">
      {/* HEADER TOP BAR */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={() => navigate("/employment/employmentpage")}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-2xs transition hover:bg-slate-50 hover:text-slate-900 w-fit"
        >
          <ArrowLeft size={16} />
          <span>Back to Employment & Contracts</span>
        </button>

        {/* HR Quick Actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsEditDrawerOpen(true)}
            className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition shadow-2xs"
          >
            <Edit size={14} className="text-slate-500" />
            <span>Edit Employee</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setIsRenewing(true);
              setIsContractDrawerOpen(true);
            }}
            className="flex items-center gap-1.5 rounded-xl bg-[#147db5] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#116d9d] transition shadow-2xs"
          >
            <RefreshCw size={14} />
            <span>Renew Contract</span>
          </button>
        </div>
      </div>

      {/* EXECUTIVE HR HERO CARD */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-xs">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          {/* Identity & Basic Info */}
          <div className="flex items-center gap-5">
            <div className="relative h-20 w-20 shrink-0">
              {employee.avatar ? (
                <img
                  src={employee.avatar}
                  alt={employee.full_name}
                  className="h-full w-full rounded-2xl border border-slate-200 object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center rounded-2xl bg-[#147db5] text-xl font-bold text-white shadow-sm">
                  {getInitials(employee.full_name || employee.first_name)}
                </div>
              )}
              <span
                className={`absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full border-2 border-white ${
                  employee.status === "Active" ? "bg-emerald-500" : "bg-amber-500"
                }`}
              />
            </div>

            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-slate-900">
                  {employee.full_name || `${employee.first_name || ""} ${employee.last_name || ""}`.trim()}
                </h1>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                  {employee.status || "Active"}
                </span>
              </div>
              <p className="mt-1 text-xs font-medium text-slate-500">
                {employee.designation || "No Designation"} •{" "}
                <span className="font-semibold text-[#147db5]">{employee.employee_id || employee.employee_code || "EMP-N/A"}</span>
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                {employee.department_name && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                    <Building size={13} className="text-slate-400" />
                    {employee.department_name}
                  </span>
                )}
                {employee.employment_type_name && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2.5 py-1 font-medium text-blue-700">
                    <Briefcase size={13} className="text-blue-500" />
                    {employee.employment_type_name}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Contact Fast-Actions */}
          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4 lg:border-t-0 lg:pt-0">
            {employee.email && (
              <a
                href={`mailto:${employee.email}`}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
              >
                <Mail size={14} className="text-slate-400" />
                <span>{employee.email}</span>
              </a>
            )}
            {employee.primary_phone && (
              <a
                href={`tel:${employee.primary_phone}`}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
              >
                <Phone size={14} className="text-slate-400" />
                <span>{employee.primary_phone}</span>
              </a>
            )}
          </div>
        </div>

        {/* HR STATS SNAPSHOT CARDS */}
        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-slate-100 pt-6 sm:grid-cols-4">
          <div className="rounded-xl bg-slate-50/80 p-3.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 block mb-1">Company Tenure</span>
            <span className="text-sm font-bold text-slate-800">{calculateTenure(employee.joining_date)}</span>
            <span className="text-[11px] text-slate-500 block mt-0.5">Joined {formatDate(employee.joining_date)}</span>
          </div>

          <div className="rounded-xl bg-slate-50/80 p-3.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 block mb-1">Contract Status</span>
            <span className="text-sm font-bold text-slate-800">{employee.contract_type || "Employment"}</span>
            <span className="text-[11px] text-slate-500 block mt-0.5">
              {employee.days_left !== null && employee.days_left !== undefined ? `${employee.days_left}d Left` : "Permanent"}
            </span>
          </div>

          <div className="rounded-xl bg-slate-50/80 p-3.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 block mb-1">Work Schedule</span>
            <span className="text-sm font-bold text-slate-800">{employee.work_schedule_name || "General Shift"}</span>
            <span className="text-[11px] text-slate-500 block mt-0.5">Regular Shift Hours</span>
          </div>

          <div className="rounded-xl bg-slate-50/80 p-3.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 block mb-1">Reporting Line</span>
            <span className="text-sm font-bold text-slate-800 truncate block">{employee.reporting_manager_name || employee.manager_name || "Vikas Ram Jadhav"}</span>
            <span className="text-[11px] text-slate-500 block mt-0.5">Direct Reporting Manager</span>
          </div>
        </div>

        {/* HR TAB BAR */}
        <div className="mt-8 flex border-b border-slate-200 gap-6 overflow-x-auto">
          {[
            { id: "overview", label: "Overview & Identity", icon: User },
            { id: "employment", label: "Employment & Role", icon: Briefcase },
            { id: "contract", label: "Contract & Legal Terms", icon: FileText },
            { id: "schedule", label: "Work Schedule & Shifts", icon: Clock },
            { id: "history", label: "Career & Audit History", icon: Calendar },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-3.5 text-xs font-semibold transition border-b-2 -mb-px whitespace-nowrap ${
                  isActive
                    ? "border-[#147db5] text-[#147db5]"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon size={15} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* TAB CONTENT VIEWS */}
      <div className="mt-6">
        {/* 1. OVERVIEW & IDENTITY */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs">
              <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                <User size={16} className="text-[#147db5]" /> Personal Profile
              </h3>
              <div className="divide-y divide-slate-100 text-xs">
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">First Name</span>
                  <span className="font-semibold text-slate-800">{employee.first_name || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Last Name</span>
                  <span className="font-semibold text-slate-800">{employee.last_name || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Preferred / Nick Name</span>
                  <span className="font-semibold text-slate-800">{employee.preferred_name || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Gender</span>
                  <span className="font-semibold text-slate-800 capitalize">{employee.gender || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Date of Birth</span>
                  <span className="font-semibold text-slate-800">{formatDate(employee.date_of_birth)}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Blood Group</span>
                  <span className="font-semibold text-slate-800">{employee.blood_group || "—"}</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs">
              <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Mail size={16} className="text-[#147db5]" /> Official & Personal Contact Details
              </h3>
              <div className="divide-y divide-slate-100 text-xs">
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Official Email</span>
                  <span className="font-semibold text-[#147db5]">{employee.official_email || employee.email || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Personal Email</span>
                  <span className="font-semibold text-slate-800">{employee.personal_email || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Primary Phone</span>
                  <span className="font-semibold text-slate-800">{employee.primary_phone || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Secondary Phone</span>
                  <span className="font-semibold text-slate-800">{employee.secondary_phone || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Nationality</span>
                  <span className="font-semibold text-slate-800">{employee.nationality || "—"}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2. EMPLOYMENT & ROLE */}
        {activeTab === "employment" && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs">
              <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Briefcase size={16} className="text-[#147db5]" /> Employment Assignment
              </h3>
              <div className="divide-y divide-slate-100 text-xs">
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Department</span>
                  <span className="font-semibold text-slate-800">{employee.department_name || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Designation / Role</span>
                  <span className="font-semibold text-slate-800">{employee.designation || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Employment Category</span>
                  <span className="font-semibold text-slate-800">{employee.employment_type_name || "—"}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Reporting Manager</span>
                  <span className="font-semibold text-slate-800">{employee.reporting_manager_name || employee.manager_name || "Vikas Ram Jadhav"}</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs">
              <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                <UserCheck size={16} className="text-[#147db5]" /> HR Status & Milestones
              </h3>
              <div className="divide-y divide-slate-100 text-xs">
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Date of Joining</span>
                  <span className="font-semibold text-slate-800">{formatDate(employee.joining_date)}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Employment Status</span>
                  <span className="font-semibold text-emerald-700 flex items-center gap-1">
                    <CheckCircle2 size={14} /> {employee.status || "Active"}
                  </span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-slate-500 font-medium">Probation Period</span>
                  <span className="font-semibold text-slate-800">Completed / Exempt</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 3. CONTRACT & LEGAL TERMS */}
        {activeTab === "contract" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs max-w-3xl">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <FileText size={16} className="text-[#147db5]" /> Active Employment Contract & Terms
            </h3>
            <div className="divide-y divide-slate-100 text-xs">
              <div className="flex justify-between py-3">
                <span className="text-slate-500 font-medium">Contract Type</span>
                <span className="font-semibold text-slate-800">{employee.contract_type || "Employment Agreement"}</span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-slate-500 font-medium">Contract Number</span>
                <span className="font-semibold text-slate-800">{employee.contract_number || "CNT-2026-REG"}</span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-slate-500 font-medium">Effective Start Date</span>
                <span className="font-semibold text-slate-800">{formatDate(employee.start_date || employee.joining_date)}</span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-slate-500 font-medium">Contract End Date</span>
                <span className="font-semibold text-slate-800">{formatDate(employee.end_date)}</span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-slate-500 font-medium">Agreement Duration</span>
                <span className="font-semibold text-slate-800">{employee.duration || "Permanent"}</span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-slate-500 font-medium">Notice Period</span>
                <span className="font-semibold text-slate-800">30 Days Standard</span>
              </div>
            </div>
          </div>
        )}

        {/* 4. WORK SCHEDULE & SHIFTS */}
        {activeTab === "schedule" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs max-w-3xl">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Clock size={16} className="text-[#147db5]" /> Assigned Shift & Working Calendar
            </h3>
            <div className="divide-y divide-slate-100 text-xs">
              <div className="flex justify-between py-3">
                <span className="text-slate-500 font-medium">Schedule Name</span>
                <span className="font-semibold text-slate-800">{employee.work_schedule_name || "General Shift"}</span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-slate-500 font-medium">Working Hours</span>
                <span className="font-semibold text-slate-800">09:00 AM – 06:00 PM</span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-slate-500 font-medium">Weekly Off Days</span>
                <span className="font-semibold text-slate-800">Saturday, Sunday</span>
              </div>
            </div>
          </div>
        )}

        {/* 5. CAREER & AUDIT HISTORY */}
        {activeTab === "history" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs max-w-3xl">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar size={16} className="text-[#147db5]" /> Employment Audit Timeline
            </h3>
            {history.length > 0 ? (
              <div className="relative border-l-2 border-slate-100 pl-4 space-y-6 text-xs my-4">
                {history.map((item, idx) => (
                  <div key={item.uuid || idx} className="relative">
                    <div className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#147db5] ring-4 ring-white" />
                    <span className="text-[11px] font-semibold text-slate-400">{formatDate(item.created_at || item.effective_date)}</span>
                    <h4 className="font-bold text-slate-800 text-sm mt-0.5">{item.change_type || "Contract Update"}</h4>
                    <p className="text-slate-600 mt-1">{item.description || item.remarks || "Employment record modified by HR."}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-xs text-slate-500">
                <p>Initial employment record established on <span className="font-semibold text-slate-700">{formatDate(employee.joining_date)}</span>.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* DRAWERS FOR ACTIONS */}
      {isEditDrawerOpen && (
        <EditEmployeeDrawer
          isOpen={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          employee={employee}
          onSave={(updatedData) => {
            setEmployee((prev) => ({ ...prev, ...updatedData }));
            setIsEditDrawerOpen(false);
          }}
          departments={departments}
          employmentTypes={employmentTypes}
          workSchedules={workSchedules}
        />
      )}

      {isContractDrawerOpen && (
        <ContractFormDrawer
          isOpen={isContractDrawerOpen}
          onClose={() => setIsContractDrawerOpen(false)}
          initialData={employee}
          isRenewing={isRenewing}
          onSubmit={async (payload) => {
            if (isRenewing) {
              await renewEmploymentContract(employee.contract_uuid || employee.uuid, payload);
            } else {
              await updateEmploymentContract(employee.contract_uuid || employee.uuid, payload);
            }
            setEmployee((prev) => ({ ...prev, ...payload }));
            setIsContractDrawerOpen(false);
          }}
          employees={[employee]}
        />
      )}
    </div>
  );
}
